import { useState } from "react";
import { Notify } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import type { MachineDetail } from "@/features/lines/hooks";
import type { ParameterDetail } from "@/features/line-editor/hooks";
import type { ParameterCategory } from "@/types/parameters.types";
import { useMachineSidebarBridge } from "../hooks/useMachineSidebarBridge";
import type { SidebarMode } from "../MachineSidebar";
import { CategoryPicker } from "./CategoryPicker";
import { ParameterRow } from "./ParameterRow";
import { CounterList } from "./CounterList";
import { ParameterForm } from "./ParameterForm";
import { EMPTY_FORM, emptyFormFor, formFromParameter, type ParamForm } from "./types";
import { useParameterActions } from "./useParameterActions";

interface Props {
  machine: MachineDetail;
  lineId: string;
  mode: SidebarMode;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ParametersTab({ machine, lineId, mode, onDirtyChange }: Props) {
  const isView = mode === "view";
  const bridge = useMachineSidebarBridge(machine.id, lineId);
  const parameters = bridge.parameters;
  const [isPending, setIsPending] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ParamForm>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [pickingCategory, setPickingCategory] = useState(false);

  // Spec 081 — os contadores COM sentido saem para a lista própria: é ali que produto, sentido e
  // equivalência aparecem. Um parâmetro nunca cai nas duas listas.
  const counters = parameters.filter((p) => p.productionRole != null);
  const plainParameters = parameters.filter((p) => p.productionRole == null);

  const hasStatusParam = parameters.some((p) => p.category === "STATUS");
  const disabledCategories: ParameterCategory[] = hasStatusParam ? ["STATUS"] : [];

  function startCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCreating(false);
    setPickingCategory(true);
    onDirtyChange?.(true);
  }

  function handleCategorySelect(category: ParameterCategory) {
    setForm(emptyFormFor(category));
    setPickingCategory(false);
    setCreating(true);
  }

  function startEdit(p: ParameterDetail) {
    setEditingId(p.id);
    setCreating(false);
    setPickingCategory(false);
    setForm(formFromParameter(p));
    onDirtyChange?.(true);
  }

  function cancelForm() {
    setEditingId(null);
    setCreating(false);
    setPickingCategory(false);
    setForm(EMPTY_FORM);
    onDirtyChange?.(false);
  }

  // Salvar e remover moram no hook ao lado (max-lines 200): os dois têm passo extra — a troca do
  // papel visual e a confirmação por nome — que não cabem no orquestrador.
  const { handleSubmit, handleDelete } = useParameterActions({
    bridge,
    form,
    editingId,
    setIsPending,
    onDone: cancelForm,
  });

  const showForm = !isView && (creating || editingId !== null);
  const showPicker = !isView && pickingCategory;

  if (bridge.parametersIsError) {
    return (
      <Notify.QueryError
        error={bridge.parametersError}
        onRetry={bridge.refetchParameters}
        variant="inline"
      />
    );
  }

  return (
    <div className="space-y-3">
      {parameters.length === 0 && !showForm && !showPicker ? (
        <p className="text-sm text-muted-foreground">Nenhum parâmetro cadastrado.</p>
      ) : null}

      <CounterList
        counters={counters}
        lineUnit={machine.lineProductionUnit ?? null}
        isView={isView}
        removePending={isPending}
        onEdit={startEdit}
        onDelete={(p) => void handleDelete(p)}
      />

      <ul className="space-y-1.5">
        {plainParameters.map((p) => {
          const hasSensorLink = (machine.parameters ?? []).some(
            (mp) => mp.id === p.id && (mp.sensorLinks?.length ?? 0) > 0
          );
          return (
            <ParameterRow
              key={p.id}
              param={p}
              hasSensorLink={hasSensorLink}
              isView={isView}
              removePending={isPending}
              onEdit={() => startEdit(p)}
              onDelete={() => void handleDelete(p)}
            />
          );
        })}
      </ul>

      {showPicker ? (
        <CategoryPicker
          disabledCategories={disabledCategories}
          onSelect={handleCategorySelect}
          onCancel={cancelForm}
        />
      ) : null}

      {showForm ? (
        <ParameterForm
          form={form}
          setForm={setForm}
          editingId={editingId}
          isPending={isPending}
          onSubmit={() => void handleSubmit()}
          onCancel={cancelForm}
          existingCounters={counters}
        />
      ) : !showPicker && !isView ? (
        <Button size="sm" variant="outline" className="w-full" onClick={startCreate}>
          + Adicionar parâmetro
        </Button>
      ) : null}
    </div>
  );
}
